// Bean record helpers that depend on nothing but constants: construction, Big Shake stats and
// per-life resets. Kept free of map/mode imports so leaf modules can use them without cycles.
import { NO_SLOT, TUNE } from '../constants';
import { BS, Cause, ItemKind } from '../ids';
import type { Bean } from '../types';

export function createBean(slot: number): Bean {
  return {
    slot, active: false, team: 0, state: BS.NORMAL, st: 0,
    pos: { x: 0, y: 0 }, vel: { x: 0, y: 0 }, z: 0, vz: 0, facing: 0,
    radius: TUNE.bean.radius, mass: TUNE.bean.mass, kbDealt: 1, kbTaken: 1,
    dizzy: 0, lastHitTick: 0,
    cdShove: 0, cdGrab: 0, cdLedge: 0, cdPebble: 0, cdCup: 0, cdVend: 0,
    lungeDir: { x: 1, y: 0 }, lungeStartTick: 0, lungeHitMask: 0,
    partner: NO_SLOT, escape: 0,
    edgePoint: { x: 0, y: 0 }, edgeNormal: { x: 1, y: 0 },
    item: ItemKind.NONE, charges: 0,
    shakeTicks: 0, floatTicks: 0, invulnTicks: 0, potatoImmuneTicks: 0,
    jumpBufferTicks: 0, coyoteTicks: 0, groundZ: 0,
    touchBy: NO_SLOT, touchTick: 0, touchCause: Cause.NONE, touchAux: 0,
    out: false, place: 0, koTick: 0, koBy: NO_SLOT,
    respawnTicks: 0, crownTicks: 0, hasPotato: false, holdsCrown: false,
    readyTicks: 0,
    prev: { shove: 0, grab: 0, jump: 0, item: 0, honk: 0, emote: 0, mxSign: 0, mySign: 0 },
  };
}

/** Big Shake derived stats (§3.3), recomputed every tick in step phase 2 and on snapshot apply. */
export function deriveBeanStats(b: Bean): void {
  const big = b.shakeTicks > 0;
  b.radius = big ? TUNE.shake.radius : TUNE.bean.radius;
  b.mass = big ? TUNE.shake.mass : TUNE.bean.mass;
  b.kbDealt = big ? TUNE.shake.kbDealt : 1;
  b.kbTaken = big ? TUNE.shake.kbTaken : 1;
}

/**
 * Clears everything that belongs to one life (state, motion, Dizzy, cooldowns, timers, credit).
 * Identity, team, position, held item and round bookkeeping (out, place, KO record) are kept.
 */
export function resetBean(b: Bean): void {
  b.state = BS.NORMAL;
  b.st = 0;
  b.vel.x = b.vel.y = 0;
  b.z = b.vz = 0;
  b.dizzy = 0;
  b.lastHitTick = 0;
  b.cdShove = b.cdGrab = b.cdLedge = b.cdPebble = b.cdCup = b.cdVend = 0;
  b.lungeStartTick = 0;
  b.lungeHitMask = 0;
  b.partner = NO_SLOT;
  b.escape = 0;
  b.shakeTicks = b.floatTicks = b.invulnTicks = b.potatoImmuneTicks = 0;
  b.jumpBufferTicks = b.coyoteTicks = 0;
  b.groundZ = 0;
  b.touchBy = NO_SLOT;
  b.touchTick = 0;
  b.touchCause = Cause.NONE;
  b.touchAux = 0;
  b.respawnTicks = 0;
  deriveBeanStats(b);
}

/** The bean occupies the arena: in play, not a ghost, not falling and not waiting to respawn. */
export function hasBody(b: Bean): boolean {
  return b.active && !b.out && b.state !== BS.GHOST && b.state !== BS.FALLING && !(b.state === BS.RESPAWN && b.respawnTicks > 0);
}
